import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import ActionRow from '../ui/ActionRow';
import { Dialog, DialogContent, DialogTitle } from '../pilot-ui/dialog';
import { Checkbox } from '../pilot-ui/checkbox';
import { Input } from '../pilot-ui/input';
import { RadioGroup, RadioGroupItem } from '../pilot-ui/radio-group';
import { useReturnFocusTarget } from '../returnFocus';
import { X } from 'lucide-react';
import LabelPhotoImage from '../LabelPhotoImage';
import { LabelPhotoOpener, fileLinkTitle } from '../LabelPhotoViewer';
import RotatePhotoButton from './RotatePhotoButton';
import {
  EQUIPMENT_EDIT_ERRORS,
  MIN_REASON_LENGTH,
  PhotoReview,
  PhotoReviewRow,
  PhotoReviewTab,
  PhotoReviewTarget,
  ReviewFix,
  ReviewFixName,
  ReviewFixResult,
  ReviewFixRow,
  applyPhotoReview,
  declinePhotoReview,
  capacityHeldMessage,
  equipmentErrorCode,
  equipmentErrorDetails,
  equipmentErrorMessage,
  fixCatalogReview,
  getEquipmentDocumentFileUrl,
  getPhotoReview,
  newEditId,
  reviewKindOf,
} from '../../api/equipmentEdit';
import { displayValue, specFor } from './equipmentEditFields';
import { own } from '../../utils/ownKey';
import {
  KIND_WORDS,
  TAB_WORDS,
  alternativeWords,
  catalogHeldWords,
  catalogTypeWords,
  componentWords,
  decidedWords,
  fixActionLabel,
  fixCatalogAfterWords,
  fixDecisionWords,
  fixNameWords,
  fixPhotoBlockWords,
  fixPhotoLabel,
  fixReasonWords,
  groupedNumber,
  heldWords,
  kindOfPath,
  sourceWords,
  tabOfComponent,
} from './photoReviewWords';

/**
 * FS-784 — review the values read off an operator's label photo before any of
 * them reaches the homeowner's record.
 *
 * The read wrote nothing. This dialog shows what it found against what each
 * candidate record holds now, lets the operator choose the record and the
 * values, and sends back only ids and the server's own opaque tokens. Every
 * value that lands is the one the server stored when it read the label — the
 * dialog formats values, it never converts, rounds or retypes them — and the
 * server, not the dialog, decides which boxes start ticked.
 *
 * The write itself happens behind the API (a Function applies it with the same
 * code that planned it), so Apply returns at once and the dialog follows the
 * review until it says how it ended. Every way it can end is said in words:
 * applied, refused (and why), refreshed, failed, or no longer applicable.
 *
 * FS-787 — the same dialog reviews catalog values (`kind: "catalog"`): what
 * the equipment catalog has for a model a record already carries, from the
 * manufacturer's literature. One record, so no target chooser; no photo, so no
 * thumbnail and no wrong-tab sentence; each row says where in which document
 * the value came from and can open that document. Rows, ticks, tokens and the
 * Apply body are the photo review's, unchanged.
 */

/**
 * How a status poll is paced (contract §8.4), by ELAPSED time from when the
 * poll started — never by how many requests came back, so a slow or hung
 * server cannot stretch the window (Codex F13).
 * - reading: every 3 s for 2 minutes, then every 15 s until 7 minutes, then stop.
 * - applying: every 1.5 s for 60 s ("taking longer" after that), then every
 *   15 s until 11 minutes — just past the server's 10-minute retry window, so
 *   a stuck Apply is seen as stuck — then stop.
 * - resync: after an Apply, every 1.5 s for 60 s while the device summary
 *   refresh has not reported, then stop.
 */
export type ReviewPollMode = 'reading' | 'applying' | 'resync' | 'check';

export const REVIEW_POLL_WINDOWS: Record<
  ReviewPollMode,
  { fastMs: number; fastEvery: number; untilMs: number; slowEvery: number }
> = {
  reading: { fastMs: 120_000, fastEvery: 3000, untilMs: 420_000, slowEvery: 15_000 },
  applying: { fastMs: 60_000, fastEvery: 1500, untilMs: 660_000, slowEvery: 15_000 },
  resync: { fastMs: 60_000, fastEvery: 1500, untilMs: 60_000, slowEvery: 1500 },
  // A one-off look at a review the record list stopped naming: ask now,
  // retry every 5 s for 30 s; the caller stops it on the first answer.
  check: { fastMs: 30_000, fastEvery: 5000, untilMs: 30_000, slowEvery: 5000 },
};

/** The wait before the next poll, `elapsedMs` into the window, or null once it is over. */
export function reviewPollDelay(mode: ReviewPollMode, elapsedMs: number): number | null {
  const w = REVIEW_POLL_WINDOWS[mode];
  if (elapsedMs >= w.untilMs) return null;
  return elapsedMs < w.fastMs ? w.fastEvery : w.slowEvery;
}

/** A poll that failed, in words — distinct from "still reading". */
export function pollErrorText(err: unknown): string {
  if (equipmentErrorCode(err)) return equipmentErrorMessage(err);
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : 'no response from the server';
}

/** Where a status poll stands, for the words around it. */
export interface ReviewPollState {
  /** The window is over and nothing more will be asked until Check again. */
  gaveUp: boolean;
  /** Two fetches in a row failed; cleared by any success. */
  fetchError: string | null;
  /** Past the fast part of the window ("Taking longer than usual…"). */
  slow: boolean;
  /**
   * Codex N4/M1 — the last answer was a refusal (403/404/409: out of this
   * device's scope, or gone). It is NOT final: after a Clip moves, the API
   * refuses until the Function records the review's terminal status, then
   * answers with it. So the poll keeps asking until its deadline, and any
   * later answer clears this.
   */
  refused: { code: string | null; message: string } | null;
}

export const IDLE_POLL: ReviewPollState = { gaveUp: false, fetchError: null, slow: false, refused: null };

/** The HTTP statuses a status poll treats as a refusal, not a passing failure. */
const REFUSAL_STATUSES = new Set([403, 404, 409]);

/**
 * Follow one review through its status view until the caller stops asking.
 *
 * `onStatus` gets every answer; the caller ends the poll by changing `mode`
 * (or setting it to null). The deadline is a timer of its own, so it lands on
 * time even while a request is still hanging; an answer that arrives after it
 * is still handed over, but nothing more is asked. `epoch` restarts the
 * window, which is what Check again does.
 */
export function useReviewStatusPoll(input: {
  serial: string;
  reviewId: string;
  mode: ReviewPollMode | null;
  epoch: number;
  onStatus: (review: PhotoReview) => void;
}): ReviewPollState {
  const { serial, reviewId, mode, epoch } = input;
  const onStatus = useRef(input.onStatus);
  onStatus.current = input.onStatus;
  const [state, setState] = useState<ReviewPollState>(IDLE_POLL);

  useEffect(() => {
    setState(IDLE_POLL);
    if (!mode) return undefined;
    const window_ = REVIEW_POLL_WINDOWS[mode];
    const started = Date.now();
    let cancelled = false;
    let expired = false;
    let failures = 0;
    let timer: number | undefined;
    const deadline = window.setTimeout(() => {
      expired = true;
      if (timer !== undefined) window.clearTimeout(timer);
      setState((s) => ({ ...s, gaveUp: true }));
    }, window_.untilMs);
    const slowTimer =
      window_.fastMs < window_.untilMs
        ? window.setTimeout(() => setState((s) => ({ ...s, slow: true })), window_.fastMs)
        : undefined;
    let asked = 0;
    const schedule = () => {
      if (cancelled || expired) return;
      // A check asks at once; every other poll waits its first interval.
      const delay = mode === 'check' && asked === 0 ? 0 : reviewPollDelay(mode, Date.now() - started);
      if (delay === null) return;
      timer = window.setTimeout(tick, delay);
    };
    const tick = async () => {
      if (cancelled || expired) return;
      asked += 1;
      try {
        const view = await getPhotoReview(serial, reviewId, { statusOnly: true });
        if (cancelled) return;
        failures = 0;
        setState((s) => (s.fetchError || s.refused ? { ...s, fetchError: null, refused: null } : s));
        onStatus.current(view);
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        if (status !== undefined && REFUSAL_STATUSES.has(status)) {
          failures = 0;
          setState((s) => ({ ...s, refused: { code: equipmentErrorCode(err), message: pollErrorText(err) } }));
          schedule();
          return;
        }
        failures += 1;
        if (failures >= 2) setState((s) => ({ ...s, fetchError: pollErrorText(err) }));
      }
      schedule();
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      window.clearTimeout(deadline);
      if (slowTimer !== undefined) window.clearTimeout(slowTimer);
    };
  }, [serial, reviewId, mode, epoch]);

  return state;
}

export interface PhotoReviewDialogProps {
  serial: string;
  reviewId: string;
  /** The reason typed with the upload, when this drawer session made it. */
  uploadReason?: string | null;
  /**
   * The newest status view the drawer's poll has for this review, or null.
   * The drawer owns every poll (and so the Apply's tracking and write hold),
   * so closing this dialog never stops following an Apply.
   */
  liveStatus: PhotoReview | null;
  /** Where the drawer's poll for this review stands. */
  poll: ReviewPollState;
  /**
   * Codex N6 — another review in this drawer is being applied. Apply waits
   * for it, like every other write in the drawer.
   */
  heldByOther?: boolean;
  /** Close (×): no call; the review stays open to finish later. */
  onClose: () => void;
  /** Declined or dismissed: the drawer drops its banner. */
  onFinished: (reviewId: string) => void;
  /** The API accepted an Apply: the drawer starts following it. */
  onApplyStarted: (reviewId: string, applyId: string) => void;
  /** Every full review this dialog loads, so the drawer knows its status too. */
  onReviewLoaded: (review: PhotoReview) => void;
  /** Check again: restart the drawer's poll window for this review. */
  onCheckAgain: (reviewId: string) => void;
  /** Called just before the Apply request goes out (the projection baseline). */
  onBeforeApply?: () => void;
  /**
   * FS-876 — a held review's fix landed: the values were cleared or moved
   * and the review is closed. The drawer reports it and reloads the record.
   */
  onFixed?: (reviewId: string, result: ReviewFixResult, reason: string) => void;
  /**
   * FS-1079 — which way up the review's photo is drawn (the turn stored on
   * the drawer's record for that URL), and what follows a Rotate here.
   * Absent: drawn as it is, with no Rotate.
   */
  photoRotation?: { degreesOf: (url: string) => number; onRotated: () => void } | null;
}

const DEFAULT_REASON = 'Checked against the label photo';
const CATALOG_DEFAULT_REASON = "Checked against the manufacturer's literature";

const STALE_NOTICE = 'Some values changed since you opened this. Check the highlighted rows.';
/** The rows a rebuild added or removed on the operator's record, by label. */
interface RowChange {
  added: string[];
  removed: string[];
}

/**
 * FS-880 — an Apply refused because its rows were out of date
 * (`proposal_outdated`, `proposal_changed`). The server rebuilt them from the
 * record as it is and today's rules, so the operator is told nothing was
 * saved and, when known, which rows came or went. A catalog review has no
 * photo, so it never mentions one.
 */
export function refreshedNotice(catalog: boolean, change?: RowChange | null): string {
  const parts = [
    catalog
      ? 'The values on offer changed since this review was prepared, so nothing was saved.'
      : 'The record or the values on offer changed since the photo was read, so nothing was saved.',
  ];
  if (change?.added.length) parts.push(`Added: ${change.added.join(', ')}.`);
  if (change?.removed.length) parts.push(`Removed: ${change.removed.join(', ')}.`);
  parts.push(catalog ? 'Check the rows and apply again.' : 'Check the values again.');
  return parts.join(' ');
}

const LOCKED_PERSON_TITLE = 'Entered or confirmed by a person. Change it with the field editor.';
const LOCKED_REQUIRED_TITLE = 'A new record is created as this component.';
const IDENTITY_TITLE =
  "This label's model differs from the record's; tick the model number to use these values on this record.";

const BLOCKED_TITLES: Record<string, string> = {
  component_person_value:
    'A person set this record as a different part of the system than these values are for, so they cannot be applied here.',
};

/**
 * FS-1076 — the hover text of a row whose value on file a person set: the
 * operator may replace it, and an unticked row leaves it as it is.
 */
const PERSON_VALUE_TITLES = {
  different: (decided: string, source: string) =>
    `The value on file was ${decided}. Tick it to replace it with the ${source}'s value; left unticked, it stays as it is.`,
  same: (decided: string, source: string) =>
    `The value on file was ${decided}, and the ${source} says the same. Tick it to confirm it against the ${source}.`,
};

/**
 * FS-1076 — the note over the rows when the model or the part of the system
 * a person set on this record differs from the label. It is still this
 * record's label to apply: ticking the row replaces the person's value.
 */
const PERSON_DIFFERS_NOTES = {
  identity: (read: string, onFile: string, decided: string, field: string) =>
    `The label reads ${read}; the model on file, ${onFile}, was ${decided}. Tick ${field} to replace it.`,
  component: (read: string, onFile: string, decided: string, field: string) =>
    `This label is for the ${read}; this record is marked as the ${onFile}, ${decided}. Tick ${field} to change it.`,
};

/**
 * FS-806 — the "Also keep it" box when the server will not let the photo
 * leave the record it was dropped on: why, on hover. `<tab>` is that tab.
 */
export const KEEP_REQUIRED_TITLES: Record<string, (tab: string) => string> = {
  source_values_cite_photo: (tab) =>
    `Values on the ${tab} tab were read from this photo, so it stays there too. Remove it there separately if it is wrong.`,
  photo_busy: (tab) =>
    `Another read or Apply of this photo on the ${tab} tab is still running, so it stays there too.`,
  source_evidence_unresolved: (tab) =>
    `Some values on the ${tab} tab were read from this photo earlier and can't be traced to another copy, so it stays there too.`,
};

/** FS-806 — the box's hover help when it is the operator's choice. */
export const keepTitle = (tab: string): string =>
  `Leave this photo on the ${tab} tab as well, for a label that covers both parts (for example a package unit)`;

/** BTU/h after a worked-out-from figure whose source field is a BTU rating. */
const BTU_FIELDS = new Set(['cooling_capacity_btuh', 'cooling_btu']);

/**
 * FS-787 — the catalog header: "Day & Night outdoor unit (NXA6 Split System
 * Air Conditioner)". Only the parts the catalog has.
 */
function catalogModelWords(review: PhotoReview): string {
  const c = review.catalog;
  const parts = [c?.manufacturer?.trim(), catalogTypeWords(c?.equipment_type)].filter(Boolean).join(' ');
  const line = c?.product_line?.trim();
  if (!line) return parts;
  return parts ? `${parts} (${line})` : line;
}

/** The model a catalog review names: the catalog's display form, else its key. */
const catalogModelOf = (review: PhotoReview | null): string | null =>
  review?.catalog?.model_display?.trim() || review?.catalog?.model_normalized?.trim() || null;

/** The words for a field, the way the card names it. */
function fieldLabel(target: PhotoReviewTarget, field: string): string {
  const spec = specFor(target.kind, field);
  if (spec) return spec.label;
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A value exactly as returned, formatted for reading only. */
function valueText(target: PhotoReviewTarget, field: string, value: unknown): string {
  const spec = specFor(target.kind, field);
  if (spec) return displayValue(spec, value);
  if (value === null || value === undefined || value === '') return 'not on file';
  if (Array.isArray(value)) return value.map(String).join(', ');
  return String(value);
}

/** The model the label proposes for this target, if it proposes one. */
const proposedModel = (target: PhotoReviewTarget): string | null => {
  const row = target.rows.find((r) => r.field === 'modelNumber' && r.proposed);
  const value = row?.proposed?.value;
  return typeof value === 'string' && value.trim() ? value : null;
};

/** "Outdoor unit: Air Conditioner (model X)" / "New outdoor unit record (model Y)". */
export function targetOptionLabel(target: PhotoReviewTarget): string {
  if (!target.exists_now) {
    const model = proposedModel(target);
    return `New ${KIND_WORDS[target.kind]} record${model ? ` (model ${model})` : ''}`;
  }
  const kind = TAB_WORDS[target.kind].replace(' (A-coil)', '');
  const name = target.name?.trim() || 'record with no name';
  const model = target.current_model?.trim();
  return `${kind}: ${name} (${model ? `model ${model}` : 'no model on file'})`;
}

/** The target inside a sentence: "the outdoor unit (Air Conditioner)". */
export function targetShortLabel(target: PhotoReviewTarget): string {
  if (!target.exists_now) return `the new ${KIND_WORDS[target.kind]} record`;
  const name = target.name?.trim();
  return `the ${KIND_WORDS[target.kind]}${name ? ` (${name})` : ''}`;
}

/**
 * Whether the operator may tick a row: a value to add or change, or (FS-1076)
 * a person's value on file to replace.
 */
const isTickableRow = (row: PhotoReviewRow): boolean => row.state === 'selectable' || row.state === 'person_value';

/**
 * The box state the server's defaults give (Codex F8). A box the server left
 * unticked is never ticked here: a ticked row that depends on an unticked
 * parent is unticked instead, until the operator picks it — picking it then
 * ticks the parent, which is the operator's choice, not the dialog's.
 */
export function defaultChecks(target: PhotoReviewTarget): Record<string, boolean> {
  const checks: Record<string, boolean> = {};
  target.rows.forEach((row) => {
    if (isTickableRow(row) || row.state === 'locked_required') {
      checks[row.candidate_id] = row.state === 'locked_required' ? true : row.default_checked;
    }
  });
  target.rows.forEach((row) => {
    if (!row.depends_on || !checks[row.candidate_id] || !isTickableRow(row)) return;
    if (!checks[row.depends_on]) checks[row.candidate_id] = false;
  });
  return checks;
}

/**
 * The boxes for a refreshed target, keeping the operator's choices (Codex
 * F9): a row whose token did not move keeps its tick or untick; a row that
 * moved, or is new, goes back to its default and is reported as changed.
 * Used for every refreshed proposal the operator had already worked on.
 */
export function mergeChecks(
  next: PhotoReviewTarget,
  before: { tokens: Map<string, string>; checks: Record<string, boolean> } | null,
): { checks: Record<string, boolean>; changed: Set<string> } {
  const defaults = defaultChecks(next);
  if (!before) return { checks: defaults, changed: new Set() };
  const checks: Record<string, boolean> = {};
  const changed = new Set<string>();
  next.rows.forEach((row) => {
    const same = before.tokens.get(row.candidate_id) === row.current_token;
    if (!same) changed.add(row.candidate_id);
    if (!(row.candidate_id in defaults)) return;
    checks[row.candidate_id] =
      same && isTickableRow(row) && row.candidate_id in before.checks
        ? Boolean(before.checks[row.candidate_id])
        : defaults[row.candidate_id];
  });
  // A kept tick must not leave a dependent row ticked without its parent.
  next.rows.forEach((row) => {
    if (row.depends_on && checks[row.candidate_id] && isTickableRow(row) && !checks[row.depends_on]) {
      checks[row.candidate_id] = false;
    }
  });
  return { checks, changed };
}

const hasTargets = (review: PhotoReview | null): review is PhotoReview & { targets: PhotoReviewTarget[] } =>
  Boolean(review && Array.isArray(review.targets));

/** A row's tick box, as both review tables draw it. */
interface RowBox {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  title: string;
  onChange: (on: boolean) => void;
  /** A word beside the box ("Confirm"). */
  note?: string;
}

/**
 * One row of a review table: the tick box, then the row's own cells, ringed
 * when its value changed under the operator. Apply's rows and the fix's
 * (FS-876) are the same row, so they tick, disable and highlight alike.
 */
const ReviewRow: React.FC<{
  field: string;
  stale: boolean;
  box: RowBox | null;
  children: React.ReactNode;
}> = ({ field, stale, box, children }) => (
  <tr
    data-testid={`photo-review-row-${field}`}
    data-stale={stale ? 'true' : undefined}
    className={`border-t ${stale ? 'bg-status-warning ring-1 ring-status-warning-border' : ''}`}
  >
    <td className="py-1 pr-2 align-top">
      {box && (
        <span className="flex items-center gap-1">
          <Checkbox
            id={box.id}
            checked={box.checked}
            disabled={box.disabled}
            onCheckedChange={box.onChange}
            title={box.title}
          />
          <label htmlFor={box.id} className="sr-only">{box.label}</label>
          {box.note && <span className="text-xs text-gray-600">{box.note}</span>}
        </span>
      )}
    </td>
    {children}
  </tr>
);

/** A fix row's value, formatted the way the record's card shows it. */
function fixValueText(kind: PhotoReviewTab | null, field: string, value: unknown): string {
  const spec = kind ? specFor(kind, field) : undefined;
  if (spec) return displayValue(spec, value);
  if (value === null || value === undefined || value === '') return 'not on file';
  return String(value);
}

/** A field's name as a fix row says it: the card's label, else the field spaced out. */
function fixFieldLabel(kind: PhotoReviewTab | null, field: string): string {
  const spec = kind ? specFor(kind, field) : undefined;
  if (spec) return spec.label;
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** FS-876 — what a fix row does, in words, from the server's effect and note. */
export function fixRowWords(fix: ReviewFix, row: ReviewFixRow): string {
  const right = fix.right.kind ? TAB_WORDS[fix.right.kind] : 'other';
  switch (row.note) {
    case 'person_value':
      return 'Kept: entered or confirmed by a person';
    case 'empty_there':
      return fix.right.exists ? `Moves to the ${right} record` : `Moves to a new ${right} record`;
    case 'same_there':
      return `Cleared here; the ${right} record has the same value`;
    case 'differs_there':
      return `The ${right} record says ${fixValueText(fix.right.kind, row.field, row.right_value)}; cleared here only if ticked`;
    case 'no_such_field':
      return `Cleared; the ${right} record has no such field`;
    case 'other_side':
      return `Does not describe the ${right}; cleared only if ticked`;
    case 'capacity_source_person':
      return `The ${right} record's capacity source was set by a person, so this is not moved there; cleared here only if ticked`;
    default:
      return row.effect === 'move' ? `Moves to the ${right} record` : 'Cleared here';
  }
}

/**
 * FS-942 — a name's key among the fix's ticks and tokens: `name:wrong` or
 * `name:right`, the field the server names it by in a `stale_fields` refusal.
 */
export const fixNameKey = (name: Pick<ReviewFixName, 'record'>): string => `name:${name.record}`;

/**
 * FS-876 — the fix's ticks for a refreshed review, keeping the operator's
 * choices as Apply's `mergeChecks` does: a row whose token did not move keeps
 * its tick; a new or moved row goes back to the server's default and is
 * reported as changed. The model row is always ticked, a person's value never.
 * A name (FS-942) is kept or reset by its token the same way.
 */
export function mergeFixChecks(
  fix: ReviewFix,
  before: { tokens: Map<string, string>; checks: Record<string, boolean> } | null,
): { checks: Record<string, boolean>; changed: Set<string> } {
  const checks: Record<string, boolean> = {};
  const changed = new Set<string>();
  const keep = (key: string, token: string, fallback: boolean) => {
    const same = before !== null && before.tokens.get(key) === token;
    if (before !== null && !same) changed.add(key);
    checks[key] = same && key in before!.checks ? Boolean(before!.checks[key]) : fallback;
  };
  fix.rows.forEach((row) => {
    if (row.state === 'locked_required' || row.state === 'locked_person_value') {
      if (before !== null && before.tokens.get(row.field) !== row.current_token) changed.add(row.field);
      checks[row.field] = row.state === 'locked_required';
    } else {
      keep(row.field, row.current_token, row.default_checked);
    }
  });
  (fix.names ?? []).forEach((name) => keep(fixNameKey(name), name.current_token, name.default_checked));
  return { checks, changed };
}

/**
 * Where a refusal from the apply step leaves the operator, in words. FS-838:
 * a capacity hold names the ticked fields the refusal recorded.
 */
function lastApplyText(code: string | null, fields: readonly string[] | null | undefined, catalog: boolean): string | null {
  if (!code) return null;
  const held = capacityHeldMessage(code, fields);
  if (held) return held;
  if (isRefreshCode(code)) return refreshedNotice(catalog);
  if (code === 'stale_fields') return STALE_NOTICE;
  return own(EQUIPMENT_EDIT_ERRORS, code) ?? `The values were not applied (${code}). Nothing was changed.`;
}

/** The refusals that mean the rows were rebuilt (FS-880). */
const isRefreshCode = (code: string | null | undefined): boolean =>
  code === 'proposal_outdated' || code === 'proposal_changed';

/**
 * FS-880 — which rows a rebuild added or removed on the record the operator
 * had chosen, by label. Null when either side is unknown.
 */
function rowChange(before: PhotoReviewTarget | null, full: PhotoReview | null): RowChange | null {
  if (!before || !full || !hasTargets(full)) return null;
  const after = full.targets.find((t) => t.id === before.id);
  if (!after) return null;
  const was = new Set(before.rows.map((r) => r.field));
  const now = new Set(after.rows.map((r) => r.field));
  return {
    added: Array.from(now).filter((f) => !was.has(f)).map((f) => fieldLabel(after, f)),
    removed: Array.from(was).filter((f) => !now.has(f)).map((f) => fieldLabel(before, f)),
  };
}

/** A sentence plus the server's code behind it, for the `title`. */
interface Said {
  text: string;
  code: string | null;
}

const PhotoReviewDialog: React.FC<PhotoReviewDialogProps> = ({
  serial,
  reviewId,
  uploadReason,
  liveStatus,
  poll,
  heldByOther = false,
  onClose,
  onFinished,
  onApplyStarted,
  onReviewLoaded,
  onCheckAgain,
  onBeforeApply,
  onFixed,
  photoRotation = null,
}) => {
  const headingId = useId();
  /**
   * FS-1079 — a turn saved from this dialog, with the stored turn it
   * replaced: shown at once, until the drawer's reload brings a new value.
   */
  const [turned, setTurned] = useState<{ degrees: number; over: number } | null>(null);
  /** The thumbnail URL that failed to draw, so a new URL gets its own try. */
  const [failedThumb, setFailedThumb] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const returnFocus = useReturnFocusTarget();
  const [review, setReview] = useState<PhotoReview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadEpoch, setLoadEpoch] = useState(0);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [stale, setStale] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<Said | null>(null);
  const [actionError, setActionError] = useState<Said | null>(null);
  const [reason, setReason] = useState(() => {
    const typed = (uploadReason ?? '').trim();
    return typed.length >= MIN_REASON_LENGTH ? typed : DEFAULT_REASON;
  });
  const [reasonError, setReasonError] = useState<string | null>(null);
  /** The operator has typed in the reason box, so a prefill never replaces it. */
  const reasonTouched = useRef(false);
  /** FS-787 — a manufacturer document being opened, and per document why it would not open. */
  const [docBusy, setDocBusy] = useState<number | null>(null);
  const [docErrors, setDocErrors] = useState<Record<number, string>>({});
  /**
   * FS-806 — "Also keep it on <source tab>": the operator's choice for the
   * target on screen. Unchecked by default and reset whenever the target
   * changes, so a keep chosen for one record is never carried to another.
   */
  const [keepOnSource, setKeepOnSource] = useState(false);
  /** An Apply this dialog sent, until the drawer's poll reports on it. */
  const [sentApply, setSentApply] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /**
   * Codex N5 — the rows could not be re-read. The review's status is already
   * the server's; this says the rows on screen may be out of date and offers
   * to read them again, keeping every choice.
   */
  const [refreshError, setRefreshError] = useState<{ message: string; staleFields: string[] } | null>(null);
  /** Codex M2 — the rows are being read again; Apply waits for them. */
  const [refreshing, setRefreshing] = useState(false);
  /**
   * FS-876 — a held review's fix: the ticks by field, the rows whose value
   * moved under the operator, the photo's tick, and the fix's own reason.
   * The reason starts empty: it is the record of why these values were on
   * the wrong record, so it is typed, never prefilled.
   */
  const [fixChecks, setFixChecks] = useState<Record<string, boolean>>({});
  const [fixStale, setFixStale] = useState<Set<string>>(new Set());
  const [photoTick, setPhotoTick] = useState(false);
  const [fixReason, setFixReason] = useState('');
  /** What the operator has chosen on the fix so far, for a merge with refreshed rows. */
  const fixChoicesRef = useRef<{
    tokens: Map<string, string>;
    checks: Record<string, boolean>;
    photo: string | null;
    photoTick: boolean;
  } | null>(null);
  /**
   * Codex M2 — bumped by every operation that makes earlier row reads stale
   * (a new read, an Apply). A full review that comes back for an earlier
   * generation is ignored, so it can never roll a newer Apply back.
   */
  const opGen = useRef(0);
  const onReviewLoadedRef = useRef(onReviewLoaded);
  onReviewLoadedRef.current = onReviewLoaded;

  const targets = hasTargets(review) ? review.targets : [];
  const target = targets.find((t) => t.id === targetId) ?? null;
  const rows = target?.rows ?? [];

  /** What the operator has chosen so far, for a merge with refreshed rows. */
  const choicesRef = useRef<{ tokens: Map<string, string>; checks: Record<string, boolean> } | null>(null);
  choicesRef.current = target
    ? { tokens: new Map(rows.map((r) => [r.candidate_id, r.current_token])), checks }
    : null;
  const targetIdRef = useRef(targetId);
  targetIdRef.current = targetId;

  /**
   * A fresh full review. With `keep`, the operator's chosen target and every
   * untouched choice survive (rows whose token moved are reset and
   * highlighted); without, the server's defaults are taken.
   */
  const adoptFull = useCallback((full: PhotoReview, keep: boolean, alsoStale: string[] = []) => {
    setReview(full);
    onReviewLoadedRef.current(full);
    // FS-876 — the fix's ticks survive a refresh on every row whose token
    // did not move; the photo's, while it is the same photo.
    const fix = full.fix ?? null;
    if (fix) {
      const before = keep ? fixChoicesRef.current : null;
      const merged = mergeFixChecks(fix, before);
      const flagged = new Set(merged.changed);
      alsoStale.forEach((field) => {
        if (fix.rows.some((r) => r.field === field) || (fix.names ?? []).some((n) => fixNameKey(n) === field)) {
          flagged.add(field);
        }
      });
      setFixChecks(merged.checks);
      setFixStale(flagged);
      const samePhoto = before !== null && fix.photo !== null && before.photo === fix.photo.url;
      setPhotoTick(samePhoto ? before.photoTick : fix.photo?.default_checked ?? false);
    }
    if (!hasTargets(full)) return;
    const prevId = targetIdRef.current;
    const chosen = keep && prevId && full.targets.some((t) => t.id === prevId) ? prevId : full.default_target_id;
    if (chosen !== prevId) setKeepOnSource(false);
    setTargetId(chosen);
    const next = full.targets.find((t) => t.id === chosen);
    if (!next) {
      setChecks({});
      setStale(new Set());
      return;
    }
    const before = keep && chosen === prevId ? choicesRef.current : null;
    const merged = mergeChecks(next, before);
    const flagged = new Set(before ? merged.changed : []);
    const fields = new Set(alsoStale);
    next.rows.forEach((r) => {
      if (fields.has(r.field)) flagged.add(r.candidate_id);
    });
    setChecks(merged.checks);
    setStale(flagged);
  }, []);

  // The full review when the dialog opens (and on Check again after a failure).
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getPhotoReview(serial, reviewId)
      .then((full) => {
        if (cancelled) return;
        adoptFull(full, false);
        // A catalog review was checked against the literature, not a photo.
        if (reviewKindOf(full) === 'catalog' && !reasonTouched.current && !(uploadReason ?? '').trim()) {
          setReason(CATALOG_DEFAULT_REASON);
        }
        if (full.last_apply?.result === 'refused') {
          setNotice({
            text: lastApplyText(full.last_apply.code, full.last_apply.fields, reviewKindOf(full) === 'catalog') ?? '',
            code: full.last_apply.code,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(pollErrorText(err));
      });
    return () => {
      cancelled = true;
    };
    // uploadReason is read once, as the initial reason is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial, reviewId, loadEpoch, adoptFull]);

  const refetchFull = useCallback(
    async (keep: boolean, alsoStale: string[] = []) => {
      opGen.current += 1;
      const gen = opGen.current;
      setRefreshing(true);
      try {
        const full = await getPhotoReview(serial, reviewId);
        if (gen !== opGen.current) return null;
        setRefreshError(null);
        adoptFull(full, keep, alsoStale);
        return full;
      } catch (err) {
        if (gen !== opGen.current) return null;
        setRefreshError({ message: pollErrorText(err), staleFields: alsoStale });
        return null;
      } finally {
        if (gen === opGen.current) setRefreshing(false);
      }
    },
    [serial, reviewId, adoptFull],
  );

  const status = review?.status ?? null;
  const catalog = reviewKindOf(review) === 'catalog';
  /**
   * FS-787 — a catalog review bound to another Clip's registration: shown,
   * never applied from here (the server would refuse it as registration_changed).
   */
  const appliesFrom = catalog ? review?.applies_from_serial ?? null : null;
  const elsewhere = appliesFrom !== null && appliesFrom.toUpperCase() !== serial.toUpperCase();
  /** An Apply is in flight: this dialog's own, or one the server reports. */
  const inFlight = sentApply !== null || status === 'applying';

  /**
   * Each new status view from the drawer's poll, once. The drawer handles an
   * applied review (it closes this dialog); here the dialog keeps its own
   * rows in step with everything else.
   */
  const seen = useRef<PhotoReview | null>(null);
  useEffect(() => {
    if (!liveStatus || liveStatus === seen.current || !review) return;
    seen.current = liveStatus;
    const view = liveStatus;
    const wasInFlight = inFlight;
    const wasReading = review.status === 'reading';
    const merge = () => setReview((prev) => (prev ? { ...prev, ...view, targets: prev.targets } : view));
    if (view.status === 'applied') return;
    if (view.status === 'applying') {
      merge();
      return;
    }
    if (wasInFlight) setSentApply(null);
    if (view.status === 'ready_for_review' && (wasInFlight || wasReading)) {
      // Refused by the apply step, or a rebuilt proposal. The status is taken
      // at once (Codex N5), so the dialog never stays "Applying" behind a slow
      // or failed re-read; then the rows are re-read and every choice the
      // operator made on an unchanged row is kept.
      const code = view.last_apply?.code ?? null;
      const refused = view.last_apply?.result === 'refused';
      const isCatalog = reviewKindOf(review) === 'catalog';
      // FS-880: the rows on screen before the rebuild, to say what changed.
      const before = hasTargets(review) ? review.targets.find((t) => t.id === targetIdRef.current) ?? null : null;
      if (wasInFlight) merge();
      const refetched = refetchFull(true, refused && code === 'stale_fields' ? view.last_apply?.fields ?? [] : []);
      if (refused || wasInFlight) {
        setNotice({ text: lastApplyText(code, view.last_apply?.fields, isCatalog) ?? refreshedNotice(isCatalog), code });
        if (isRefreshCode(code)) {
          void refetched.then((full) => {
            const change = rowChange(before, full);
            if (!change || (change.added.length === 0 && change.removed.length === 0)) return;
            // Only while this notice is still the one shown.
            setNotice((prev) => (prev && prev.code === code ? { text: refreshedNotice(isCatalog, change), code } : prev));
          });
        }
      }
      return;
    }
    if (view.status === 'reading' && wasInFlight) {
      setNotice({ text: refreshedNotice(reviewKindOf(review) === 'catalog'), code: view.last_apply?.code ?? null });
    }
    merge();
  }, [liveStatus, review, inFlight, refetchFull]);

  const byId = useMemo(() => new Map(rows.map((r) => [r.candidate_id, r])), [rows]);
  const modelRow = rows.find((r) => r.field === 'modelNumber' && r.state !== 'not_found') ?? null;

  /**
   * FS-1076 — the notes over the rows when the model or the part of the
   * system a person set on this record differs from the label.
   */
  const personNotes: string[] = [];
  if (target) {
    const noteFor = (kind: keyof typeof PERSON_DIFFERS_NOTES, field: string, words: (value: unknown) => string) => {
      const row = rows.find((r) => r.field === field && r.state === 'person_value');
      if (!row?.proposed || !row.current) return;
      personNotes.push(PERSON_DIFFERS_NOTES[kind](
        words(row.proposed.value), words(row.current.value), decidedWords(row.current), fieldLabel(target, field),
      ));
    };
    if (target.match.identity === 'differs_person') {
      noteFor('identity', 'modelNumber', (value) => valueText(target, 'modelNumber', value));
    }
    if (target.match.component === 'differs_person') {
      noteFor('component', 'equipmentSide', (value) => componentWords(String(value)));
    }
  }

  const rawChecked = (row: PhotoReviewRow) =>
    row.state === 'locked_required' ? true : isTickableRow(row) ? Boolean(checks[row.candidate_id]) : false;
  const modelTicked = modelRow ? rawChecked(modelRow) : false;
  /** The rule of §8.5: a label whose model differs needs the model ticked first. */
  const identityBlocked = (row: PhotoReviewRow) =>
    row.requires_identity && row.field !== 'modelNumber' && !modelTicked;
  const effective = (row: PhotoReviewRow) => rawChecked(row) && !identityBlocked(row);

  const selected = rows.filter(effective);
  const isSource = target ? target.path === review?.source.path : false;

  const toggle = (row: PhotoReviewRow, on: boolean) => {
    setChecks((prev) => {
      const next = { ...prev, [row.candidate_id]: on };
      if (on && row.depends_on) {
        const parent = byId.get(row.depends_on);
        if (parent && isTickableRow(parent)) next[parent.candidate_id] = true;
      }
      if (!on) {
        rows.forEach((child) => {
          if (child.depends_on === row.candidate_id && isTickableRow(child)) {
            next[child.candidate_id] = false;
          }
        });
      }
      return next;
    });
  };

  const chooseTarget = (id: string) => {
    if (id !== targetId) setKeepOnSource(false);
    setTargetId(id);
    const next = targets.find((t) => t.id === id);
    setChecks(next ? defaultChecks(next) : {});
    setStale(new Set());
  };

  /**
   * FS-806 / FS-807 — where the photo goes on this Apply. Only a photo review
   * applied to a record other than the one the photo was dropped on, with
   * the server's placement mode on, has a choice: move it (the default) or
   * also keep it there. The server's `keep_required` makes the keep forced.
   */
  const placement = !catalog && review?.placement?.mode === true ? review.placement : null;
  const sourceTab = placement ? TAB_WORDS[placement.source_kind] ?? 'source' : '';
  const showKeep = placement !== null && target !== null && !isSource;
  const keepRequired = showKeep ? placement?.keep_required ?? null : null;
  const keeping = showKeep && (keepRequired !== null || keepOnSource);
  const moving = showKeep && !keeping;
  /** The target already carries this same photo: no second copy is added. */
  const duplicateOnTarget = showKeep && target?.duplicate_of ? target.duplicate_of : null;
  const targetWords = target ? targetShortLabel(target) : '';
  const placementLine = !showKeep
    ? null
    : duplicateOnTarget
      ? `The same photo is already on ${targetWords}; no second copy is added.${moving ? ` It comes off the ${sourceTab} tab.` : ''}`
      : moving
        ? `The photo moves to ${targetWords} and comes off the ${sourceTab} tab.`
        : `The photo stays on the ${sourceTab} tab and is added to ${targetWords}.`;

  /**
   * FS-876 — the held review's fix, when the server offers one. Every rule
   * is the server's: the rows, their ticks and what each does. The dialog
   * keeps the operator's ticks and says what the ticked rows do: Move when
   * any of them moves a value, else Clear, and the photo goes the same way.
   */
  const fix = catalog && status === 'held' ? review?.fix ?? null : null;
  const fixRows = fix?.rows ?? [];
  const fixNames = fix?.names ?? [];
  const fixTicked = (row: ReviewFixRow) =>
    row.state === 'locked_required' ? true : row.state === 'locked_person_value' ? false : Boolean(fixChecks[row.field]);
  /**
   * FS-942 — a name changes only with a ticked row it is named for: an
   * unticked brand stays on the record, and so does the name it gave. The
   * operator's tick is kept, and counts again once the row is ticked.
   */
  const nameAvailable = (name: ReviewFixName) =>
    (name.named_for ?? []).length === 0 ||
    name.named_for.some((field) => fixRows.some((row) => row.field === field && fixTicked(row)));
  const nameTicked = (name: ReviewFixName) => nameAvailable(name) && Boolean(fixChecks[fixNameKey(name)]);
  const fixMoves = fixRows.some((row) => fixTicked(row) && row.effect === 'move');
  const photoAction: 'remove' | 'move' = fixMoves ? 'move' : 'remove';
  const photoBlock = fix?.photo ? fix.photo.blocked[photoAction] : null;
  const photoGoes = Boolean(fix?.photo) && photoTick && photoBlock === null;
  const tonnageRow = fixRows.find((row) => row.field === 'tonnage' && row.state === 'selectable') ?? null;
  const consequenceText = fix?.consequence
    ? tonnageRow && !fixTicked(tonnageRow) && fix.consequence.text_without_tonnage !== null
      ? fix.consequence.text_without_tonnage
      : fix.consequence.text
    : null;
  fixChoicesRef.current = fix
    ? {
      tokens: new Map([
        ...fixRows.map((row): [string, string] => [row.field, row.current_token]),
        ...fixNames.map((name): [string, string] => [fixNameKey(name), name.current_token]),
      ]),
      checks: fixChecks,
      photo: fix.photo?.url ?? null,
      photoTick,
    }
    : null;

  /** Why the fix cannot be sent right now, or null when it can. A missing reason is said on press, not here. */
  const fixBlocker = (() => {
    if (elsewhere) return `This review is fixed from Clip ${appliesFrom}'s equipment editor.`;
    if (heldByOther) return 'Another review is being applied. Wait for it to finish, then fix this one.';
    if (refreshing) return 'The values are being read again. Fix them once they are shown.';
    if (refreshError) return 'The values could not be read again. Load them again before fixing.';
    return null;
  })();

  /** Why Apply cannot be pressed right now, or null when it can. */
  const applyBlocker = (() => {
    if (!target) {
      if (targets.some((t) => t.match.identity === 'differs' || t.match.identity === 'differs_person')) {
        return 'The model on this label differs from the model on file. Choose the record these values belong to.';
      }
      const existing = targets.filter((t) => t.exists_now);
      return existing.length > 1
        ? `This system has more than one ${KIND_WORDS[existing[0].kind]} record, so choose which one this label belongs to.`
        : 'Choose which record this label belongs to.';
    }
    if (elsewhere) return `These catalog values are applied from Clip ${appliesFrom}'s equipment editor.`;
    if (target.blocked) return BLOCKED_TITLES[target.blocked] ?? 'This record cannot take these values.';
    if (selected.length === 0 && (isSource || !target.exists_now)) {
      return 'Tick at least one value to apply.';
    }
    // FS-806 §8.3: keeping the photo where it is, on a record that already
    // has it, with nothing ticked, would change nothing at all.
    if (selected.length === 0 && keeping && duplicateOnTarget) {
      return `The same photo is already on ${targetWords} and no value is ticked, so there is nothing to apply.`;
    }
    if (reason.trim().length < MIN_REASON_LENGTH) return EQUIPMENT_EDIT_ERRORS.reason_required;
    if (heldByOther) return 'Another photo review is being applied. Wait for it to finish, then apply this one.';
    if (refreshing) return 'The values are being read again. Apply once they are shown.';
    if (refreshError) return 'The values could not be read again. Load them again before applying.';
    return null;
  })();

  const applyLabel = (() => {
    if (!target) return 'Apply';
    const n = selected.length;
    const values = `${n} ${n === 1 ? 'value' : 'values'}`;
    if (!target.exists_now) return `Create ${targetShortLabel(target)} with ${values}`;
    if (n === 0 && !catalog) {
      return moving ? `Move photo to ${targetShortLabel(target)}` : `Attach photo to ${targetShortLabel(target)}`;
    }
    return `Apply ${values} to ${targetShortLabel(target)}`;
  })();

  const apply = async () => {
    if (!review || !target) return;
    setActionError(null);
    setReasonError(null);
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
      return;
    }
    const applyId = newEditId();
    // Any row read still on its way belongs to before this Apply.
    opGen.current += 1;
    setRefreshing(false);
    // Ids and the server's own tokens only: nothing typed or shown here is
    // a value the server will write.
    const body = {
      apply_id: applyId,
      version: review.version,
      target_id: target.id,
      selected: selected.map((row) => ({ candidate_id: row.candidate_id, current_token: row.current_token })),
      reason: reason.trim(),
      // FS-806: only when the box is on screen; a forced keep is sent as one.
      ...(showKeep ? { keep_on_source: keeping } : {}),
    };
    setSending(true);
    try {
      onBeforeApply?.();
      await applyPhotoReview(serial, reviewId, body);
      setNotice(null);
      setStale(new Set());
      // 202 while it runs, or 200 once a replay finds it done: either way the
      // drawer's poll takes it from here and reports how it ended.
      setSentApply(applyId);
      setReview((prev) => (prev ? { ...prev, status: 'applying' } : prev));
      onApplyStarted(reviewId, applyId);
    } catch (err) {
      const code = equipmentErrorCode(err);
      if (code === 'stale_fields') {
        const fresh = equipmentErrorDetails(err)?.review as PhotoReview | undefined;
        const fields = (equipmentErrorDetails(err)?.fields as string[] | undefined) ?? [];
        if (fresh && hasTargets(fresh)) adoptFull(fresh, true, fields);
        else void refetchFull(true, fields);
        setNotice({ text: STALE_NOTICE, code });
        return;
      }
      if (code === 'proposal_changed') {
        const full = await refetchFull(true);
        setNotice({ text: refreshedNotice(catalog, rowChange(target, full)), code });
        return;
      }
      setActionError({ text: equipmentErrorMessage(err), code });
      if (code === 'review_not_open' || code === 'photo_removed' || code === 'registration_changed' || code === 'review_closed') {
        void refetchFull(true);
      }
    } finally {
      setSending(false);
    }
  };

  /**
   * FS-876 — clear or move the ticked values. Fields and the server's tokens
   * only, as Apply sends ids: nothing typed here is a value the server writes.
   * Pressed without a reason, it says one is needed and sends nothing.
   */
  const fixIt = async () => {
    if (!review || !fix || fix.action === null) return;
    setActionError(null);
    setReasonError(null);
    if (fixReason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
      return;
    }
    // Any row read still on its way belongs to before this fix.
    opGen.current += 1;
    setRefreshing(false);
    const reasonSent = fixReason.trim();
    const body = {
      edit_id: newEditId(),
      version: review.version,
      rows: fixRows.filter(fixTicked).map((row) => ({ field: row.field, token: row.current_token })),
      names: fixNames.filter(nameTicked).map((name) => ({ record: name.record, token: name.current_token })),
      photo: photoGoes && fix.photo ? { url: fix.photo.url, action: photoAction } : null,
      reason: reasonSent,
    };
    setSending(true);
    try {
      onBeforeApply?.();
      const result = await fixCatalogReview(serial, reviewId, body);
      setNotice(null);
      setFixStale(new Set());
      setReview((prev) => (prev ? {
        ...prev,
        status: 'applied',
        open: false,
        decision: {
          kind: result.fix,
          at: null,
          target_path: result.fix === 'moved' ? result.right.path : result.wrong.path,
          created: result.right.created,
          fields_written: result.fix === 'moved' ? result.moved : result.cleared,
          rows: [],
        },
      } : prev));
      onFixed?.(reviewId, result, reasonSent);
    } catch (err) {
      const code = equipmentErrorCode(err);
      if (code === 'stale_fields') {
        const fresh = equipmentErrorDetails(err)?.review as PhotoReview | undefined;
        const fields = (equipmentErrorDetails(err)?.fields as string[] | undefined) ?? [];
        const now = fresh ?? (await refetchFull(true, fields));
        if (fresh) adoptFull(fresh, true, fields);
        // A ticked value emptied on the record meanwhile has no row to ring;
        // a name no longer offered, none to ring either.
        const gone = now?.fix
          ? fields.filter((f) => f !== 'photo' && !now.fix!.rows.some((r) => r.field === f) &&
            !(now.fix!.names ?? []).some((n) => fixNameKey(n) === f))
          : [];
        setNotice({
          text: gone.length > 0
            ? 'Some values changed since you opened this, and some are no longer on this record. Check the rows again.'
            : STALE_NOTICE,
          code,
        });
        return;
      }
      if (code === 'proposal_changed') {
        await refetchFull(true);
        setNotice({ text: 'This review changed since it was opened, so nothing was changed. Check the rows and try again.', code });
        return;
      }
      setActionError({ text: equipmentErrorMessage(err), code });
      if (code === 'review_closed' || code === 'review_not_open' || code === 'fix_not_available' || code === 'registration_changed') {
        void refetchFull(true);
      }
    } finally {
      setSending(false);
    }
  };

  const decline = async () => {
    if (!review) return;
    setActionError(null);
    setSending(true);
    try {
      await declinePhotoReview(serial, reviewId, {
        edit_id: newEditId(),
        version: review.version,
        ...(reason.trim().length >= MIN_REASON_LENGTH ? { reason: reason.trim() } : {}),
      });
      onFinished(reviewId);
    } catch (err) {
      setActionError({ text: equipmentErrorMessage(err), code: equipmentErrorCode(err) });
    } finally {
      setSending(false);
    }
  };

  /**
   * FS-787 — open our stored copy of the manufacturer document a value came
   * from, as the Equipment Catalog page does: the tab is opened by the click
   * and pointed at the short-lived link afterwards, so a popup blocker does not
   * eat it, and the link is opened as-is (no Authorization header rides along).
   */
  const openDocument = async (documentId: number) => {
    setDocErrors((prev) => ({ ...prev, [documentId]: '' }));
    setDocBusy(documentId);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const url = await getEquipmentDocumentFileUrl(documentId);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (err) {
      tab?.close();
      setDocErrors((prev) => ({ ...prev, [documentId]: pollErrorText(err) }));
    } finally {
      setDocBusy(null);
    }
  };

  const checkAgain = () => {
    if (loadError) {
      setLoadEpoch((n) => n + 1);
      return;
    }
    onCheckAgain(reviewId);
  };

  const { gaveUp, fetchError: pollFetchError, slow } = poll;
  const fetchError = pollFetchError;
  const applyFailed = status === 'failed' && review?.last_apply?.result === 'failed';
  const stuck = Boolean(review?.applying?.stuck);
  const busy = sending || (inFlight && !gaveUp && !stuck);
  // Close stays available while an Apply runs: the drawer follows it either way.
  const canClose = !sending;

  /**
   * FS-806 — a record named by path inside a sentence: the review's own name
   * for it when it was one of the targets, else its kind ("the outdoor unit").
   */
  const recordInSentence = (path: string): string => {
    const known = targets.find((t) => t.path === path);
    if (known) return targetShortLabel(known);
    const kind = kindOfPath(path);
    return kind === 'filter' ? 'a filter record' : `the ${KIND_WORDS[kind]}`;
  };

  // ── rendering ───────────────────────────────────────────────────────────

  const component = review?.read?.component ?? null;
  const componentTab = tabOfComponent(component);
  const wrongTab =
    !catalog &&
    review && component && ((componentTab && componentTab !== review.dropped_on) || review.hint_matches === false);
  const thumb = review?.photo ? review.photo.preview_url || review.photo.url : null;
  // FS-1079: the photo as the record holds it. An upload that matched a photo
  // already on the record was read as that one, and that one is turned.
  const heldPhoto = review?.photo ? review.photo.duplicate_of || review.photo.url : null;
  const storedTurn = heldPhoto && photoRotation ? photoRotation.degreesOf(heldPhoto) : 0;
  const shownTurn = turned && turned.over === storedTurn ? turned.degrees : storedTurn;
  const thumbFailed = thumb !== null && failedThumb === thumb;
  // One Rotate, offered beside the photo and in the large view it opens.
  const rotate = heldPhoto && photoRotation && review?.source.path && review.source.kind ? (
    <RotatePhotoButton
      serial={serial}
      url={heldPhoto}
      target={{ kind: review.source.kind, path: review.source.path }}
      degrees={shownTurn}
      index={0}
      onRotated={(degrees) => {
        setTurned({ degrees, over: storedTurn });
        photoRotation.onRotated();
      }}
    />
  ) : null;
  const dropTab = review ? TAB_WORDS[review.dropped_on] : '';
  const catalogModel = catalogModelOf(review);

  const header = (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b p-4">
      <div className="flex items-start gap-3">
        {thumb && heldPhoto && !catalog && review && (
          // As wide as the wider of the thumbnail and Rotate, so Rotate stays inside it.
          <div className="w-min shrink-0">
            {thumbFailed ? (
              // Nothing drew, so there is nothing to show large: the file opens in a new tab.
              <a href={thumb} target="_blank" rel="noopener noreferrer" title="Open the label photo in a new tab">
                <LabelPhotoImage
                  src={thumb}
                  alt="Label photo"
                  degrees={shownTurn}
                  boxClassName="h-16 w-16 rounded-sm border"
                  fit="cover"
                />
              </a>
            ) : (
              <LabelPhotoOpener
                src={thumb}
                alt="Label photo"
                degrees={shownTurn}
                onError={() => setFailedThumb(thumb)}
                boxClassName="h-16 w-16 rounded-sm border"
                fit="cover"
                label="Show the label photo large"
                heading="Label photo"
                fileTitle={fileLinkTitle(thumb !== review.photo?.url)}
                rotate={rotate}
              />
            )}
            {rotate}
          </div>
        )}
        <div>
          <DialogTitle id={headingId} className="text-lg font-semibold">
            {catalog
              ? 'Catalog values'
              : component ? `${componentWords(component).replace(/^./, (c) => c.toUpperCase())} label` : 'Label photo'}
          </DialogTitle>
          {catalog && review && (
            <p data-testid="catalog-review-header" className="mt-1 text-sm text-gray-700">
              The equipment catalog now has model <strong>{catalogModel ?? 'unknown'}</strong>
              {catalogModelWords(review) ? `: ${catalogModelWords(review)}` : ''}. These values come from the
              manufacturer&apos;s literature.
            </p>
          )}
          {wrongTab && (
            <p data-testid="photo-review-wrong-tab" className="mt-1 text-sm text-gray-700">
              This is an <strong>{componentWords(component)}</strong> label. It was dropped on the{' '}
              <strong>{dropTab}</strong> tab. These are the {componentWords(component)}&apos;s details.
            </p>
          )}
        </div>
      </div>
      <Button
        variant="icon"
        size="md"
        onClick={onClose}
        disabled={!canClose}
        aria-label={catalog ? 'Close the catalog review' : 'Close the photo review'}
        title={
          inFlight
            ? 'Close; the values keep being applied and this drawer reports how it ends'
            : 'Close; this review stays here to finish later'
        }
      >
        <X data-icon-size="" className="h-5 w-5" />
      </Button>
    </div>
  );

  const errorBanner = (actionError || fetchError || refreshError || (poll.refused && inFlight)) && (
    <div role="alert" className="shrink-0 border-b border-status-error-border bg-red-100 px-4 py-2 text-sm text-red-800">
      {refreshError && (
        <p>
          {`Couldn't load the values again: ${refreshError.message}. The ones shown may be out of date.`}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void refetchFull(true, refreshError.staleFields)}
            title="Read the values again; your ticks on rows that did not change are kept"
            className="ml-2"
          >
            Load the values again
          </Button>
        </p>
      )}
      {actionError && <p title={actionError.code ? `Code: ${actionError.code}` : undefined}>{actionError.text}</p>}
      {fetchError && <p>{`Couldn't check the photo review: ${fetchError}. Retrying…`}</p>}
      {poll.refused && inFlight && (
        <p title={poll.refused.code ? `Code: ${poll.refused.code}` : undefined}>
          {`Couldn't confirm yet how applying the values ended: ${poll.refused.message} Still checking.`}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onCheckAgain(reviewId)}
            title="Ask the server again now how applying these values ended"
            className="ml-2"
          >
            Retry
          </Button>
        </p>
      )}
    </div>
  );

  const checkAgainButton = (
    <Button
      variant="secondary"
      onClick={checkAgain}
      title="Ask the server for this review's status again"
    >
      Check again
    </Button>
  );

  const dismissButton = (
    <Button
      variant="secondary"
      onClick={decline}
      disabled={sending}
      title={
        catalog
          ? 'Close this review for good; no values on the record change'
          : 'Close this review for good; the photo stays on the record and no values change'
      }
    >
      Dismiss
    </Button>
  );

  const rowsBody = (
    <div className="space-y-4 p-4" data-state={inFlight ? 'applying' : applyFailed ? 'apply-failed' : 'ready'}>
      {notice && (
        <p
          className="rounded-md border border-status-warning-border bg-status-warning p-3 text-sm text-status-warning-foreground"
          role="status"
          title={notice.code ? `Code: ${notice.code}` : undefined}
        >
          {notice.text}
        </p>
      )}
      {applyFailed && (
        <p
          className="rounded-md border border-status-error-border bg-red-50 p-3 text-sm text-red-800"
          role="alert"
          title={`Code: ${review?.last_apply?.code ?? 'apply_retry_exhausted'}`}
        >
          {lastApplyText(review?.last_apply?.code ?? 'apply_retry_exhausted', review?.last_apply?.fields, catalog)}
        </p>
      )}

      {/* A catalog review is about one record: there is nothing to choose. */}
      {!catalog && (targets.length > 1 || review?.default_target_id === null) && (
        <fieldset className="rounded-sm border p-3">
          <legend className="px-1 text-xs uppercase tracking-wide text-muted-foreground">Which record</legend>
          {review?.default_target_id === null && !target && (
            <p className="mb-2 text-sm text-gray-700">{applyBlocker}</p>
          )}
          <RadioGroup value={targetId ?? ''} onValueChange={chooseTarget} className="space-y-1">
            {targets.map((option) => {
              const id = `${headingId}-target-${option.id.replace(/[^a-zA-Z0-9]+/g, '-')}`;
              const blockedTitle = option.blocked ? BLOCKED_TITLES[option.blocked] ?? 'This record cannot take these values.' : null;
              return (
                <label key={option.id} htmlFor={id} className="flex items-center gap-2 text-sm" title={blockedTitle ?? undefined}>
                  <RadioGroupItem
                    id={id}
                    value={option.id}
                    disabled={Boolean(option.blocked) || busy}
                    title={blockedTitle ?? `Use the values from this label on ${targetShortLabel(option)}`}
                  />
                  <span className={option.blocked ? 'text-muted-foreground' : undefined}>{targetOptionLabel(option)}</span>
                </label>
              );
            })}
          </RadioGroup>
        </fieldset>
      )}

      {personNotes.length > 0 && (
        <div className="space-y-1 text-sm text-gray-700" data-testid="photo-review-person-note">
          {personNotes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      )}

      {target && (
        <table className="min-w-full text-sm" data-testid="photo-review-rows">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-2 font-medium">Use</th>
              <th className="py-1 pr-3 font-medium">Field</th>
              <th className="py-1 pr-3 font-medium">On file</th>
              <th className="py-1 pr-3 font-medium">{catalog ? 'In the catalog' : 'On the label'}</th>
              <th className="py-1 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const label = fieldLabel(target, row.field);
              const boxId = `${headingId}-row-${row.candidate_id.replace(/[^a-zA-Z0-9]+/g, '-')}`;
              const blocked = identityBlocked(row);
              const flags = row.proposed?.flags ?? [];
              const decided = row.state === 'person_value' && row.current ? decidedWords(row.current) : null;
              const sourceName = catalog ? 'catalog' : 'label';
              const title =
                row.state === 'locked_person_value'
                  ? LOCKED_PERSON_TITLE
                  : row.state === 'locked_required'
                    ? LOCKED_REQUIRED_TITLE
                    : blocked
                      ? IDENTITY_TITLE
                      : decided
                        ? PERSON_VALUE_TITLES[row.relation === 'same' ? 'same' : 'different'](decided, sourceName)
                        : row.relation === 'same'
                          ? `Confirm the value on file against the ${sourceName}; it is then marked as checked by a person`
                          : `Use this ${label.toLowerCase()} on ${targetShortLabel(target)}`;
              const notes: string[] = [];
              const provenance = row.proposed?.provenance ?? null;
              // FS-787: where in the manufacturer's document, verbatim.
              if (provenance?.locator) notes.push(provenance.locator);
              if (flags.includes('converted') && row.proposed?.derived_from) {
                const unit = BTU_FIELDS.has(row.proposed.derived_from.field) ? ' BTU/h' : '';
                notes.push(`Worked out from ${groupedNumber(row.proposed.derived_from.value)}${unit}`);
              }
              if (flags.includes('low_confidence')) notes.push(catalog ? 'medium confidence' : 'Low read confidence');
              if (flags.includes('below_identity_bar')) notes.push('Model read with low confidence');
              const documentId = catalog && typeof provenance?.document_id === 'number' ? provenance.document_id : null;
              return (
                <ReviewRow
                  key={row.candidate_id}
                  field={row.field}
                  stale={stale.has(row.candidate_id)}
                  box={row.state === 'not_found' ? null : {
                    id: boxId,
                    label: `${row.relation === 'same' ? 'Confirm' : 'Use'} ${label}`,
                    checked: effective(row),
                    disabled: !isTickableRow(row) || blocked || busy,
                    title,
                    onChange: (on) => toggle(row, on),
                    note: row.relation === 'same' ? 'Confirm' : undefined,
                  }}
                >
                  <td className="py-1 pr-3 align-top">{label}</td>
                  <td className="py-1 pr-3 align-top text-gray-600">
                    {row.current ? valueText(target, row.field, row.current.value) : 'not on file'}
                    {/* FS-1076: a person's value says who set it and when. */}
                    {decided && (
                      <span className="block text-xs text-muted-foreground" data-testid={`photo-review-decided-${row.field}`}>
                        {decided}
                      </span>
                    )}
                    {/* FS-783 §6.2: the value on file lost its evidence; the
                        reason is one hover away. */}
                    {row.current?.evidence_removed && (
                      <span
                        className="block text-xs text-amber-800"
                        data-testid={`photo-review-evidence-removed-${row.field}`}
                        title={`Removed: ${row.current.evidence_removed.reason ?? 'no reason recorded'}`}
                      >
                        photo it was read from was removed
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-3 align-top font-medium">
                    {row.proposed ? valueText(target, row.field, row.proposed.value) : (
                      <span className="font-normal text-muted-foreground">{catalog ? 'Not in the catalog' : 'Not on the label'}</span>
                    )}
                    {row.alternatives.map((alt, i) => (
                      <span key={`${alt.source}-${i}`} className="block text-xs font-normal text-muted-foreground">
                        {alternativeWords(alt.source, valueText(target, row.field, alt.value))}
                      </span>
                    ))}
                  </td>
                  <td className="py-1 align-top text-xs text-gray-600">
                    {row.proposed && <span className="block">{sourceWords(row.proposed.source, row.proposed.basis)}</span>}
                    {notes.map((n) => (
                      <span key={n} className="block">
                        {n}
                      </span>
                    ))}
                    {documentId !== null && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => void openDocument(documentId)}
                        disabled={docBusy === documentId}
                        title="Open the manufacturer's document this value came from"
                        className="mt-0.5"
                      >
                        {docBusy === documentId ? 'Opening…' : 'Open document'}
                      </Button>
                    )}
                    {documentId !== null && docErrors[documentId] && (
                      <span className="block text-red-700" role="alert">
                        {`Couldn't open the document: ${docErrors[documentId]}`}
                      </span>
                    )}
                  </td>
                </ReviewRow>
              );
            })}
          </tbody>
        </table>
      )}
      {/* FS-787 R10: only when Apply will actually re-stamp the model link. */}
      {catalog && target?.model_link_update === true && (
        <p className="text-xs text-gray-600" data-testid="catalog-review-model-note">
          The model number will be marked as catalog-verified.
        </p>
      )}

      {showKeep && (
        <label
          htmlFor={`${headingId}-keep`}
          className="flex items-center gap-2 text-sm"
          title={keepRequired ? KEEP_REQUIRED_TITLES[keepRequired]?.(sourceTab) : keepTitle(sourceTab)}
        >
          <Checkbox
            id={`${headingId}-keep`}
            data-testid="photo-review-keep-on-source"
            checked={keeping}
            disabled={keepRequired !== null || busy}
            onCheckedChange={setKeepOnSource}
            title={
              keepRequired
                ? KEEP_REQUIRED_TITLES[keepRequired]?.(sourceTab) ?? `The photo stays on the ${sourceTab} tab too (${keepRequired}).`
                : keepTitle(sourceTab)
            }
          />
          <span className={keepRequired ? 'text-muted-foreground' : undefined}>{`Also keep it on ${sourceTab}`}</span>
        </label>
      )}

      <div>
        <label htmlFor={`${headingId}-reason`} className="text-xs uppercase tracking-wide text-muted-foreground">
          Reason for this change
        </label>
        <Input
          id={`${headingId}-reason`}
          type="text"
          value={reason}
          disabled={busy}
          title="Kept with the change: say how the values were checked"
          aria-describedby={`${headingId}-reason-help${reasonError ? ` ${headingId}-reason-error` : ''}`}
          aria-invalid={Boolean(reasonError)}
          onChange={(e) => {
            reasonTouched.current = true;
            setReason(e.target.value);
          }}
          className="mt-1"
        />
        <span id={`${headingId}-reason-help`} className="mt-1 block text-xs text-muted-foreground">
          Say how the values were checked; this reason is kept with the change.
        </span>
        {reasonError && (
          <span id={`${headingId}-reason-error`} className="mt-1 block text-xs text-red-700" role="alert">
            {reasonError}
          </span>
        )}
      </div>

      {inFlight && (
        <p className="text-sm text-gray-700" role="status">
          {stuck
            ? 'Applying has not finished after 10 minutes.'
            : slow || gaveUp
              ? 'Taking longer than usual…'
              : 'Applying the values…'}
        </p>
      )}

      <ActionRow
        className="sticky bottom-0 z-10 border-t bg-popover py-2"
        reason={applyBlocker && (!inFlight || stuck) && (target ? applyBlocker : 'Choose a record before applying.')}
        reasonId={`${headingId}-apply-disabled-reason`}
      >
        {(!inFlight || stuck) && (
          <Button
            variant="primary"
            onClick={apply}
            disabled={busy || applyBlocker !== null}
            aria-describedby={applyBlocker && (!inFlight || stuck) ? `${headingId}-apply-disabled-reason` : undefined}
            title={
              applyBlocker ??
              (applyFailed || stuck
                ? 'Send the same choice again as a new request'
                : 'Write the ticked values to the record named on this button. Values left unticked stay as they are.')
            }
          >
            {applyFailed || stuck ? 'Try again' : sending ? 'Working…' : applyLabel}
          </Button>
        )}
        {inFlight && (slow || gaveUp) && !stuck && checkAgainButton}
        {!inFlight && !applyFailed && (
          <Button
            variant="secondary"
            onClick={decline}
            disabled={busy}
            title={
              catalog
                ? "Turn down these catalog values; nothing on the record changes"
                : `Keep the photo on the ${review ? TAB_WORDS[review.dropped_on] : 'record'} tab; change no values`
            }
          >
            Don&apos;t apply
          </Button>
        )}
        {applyFailed && !inFlight && dismissButton}
      </ActionRow>
      {placementLine && !inFlight && (
        <p className="text-xs text-gray-600" data-testid="photo-review-placement">
          {placementLine}
        </p>
      )}
    </div>
  );

  const body = (() => {
    if (loadError && !review) {
      return (
        <div className="flex flex-col items-start gap-3 p-4" data-state="status-error">
          <p className="text-sm text-red-800" role="alert">{`Couldn't load the photo review: ${loadError}`}</p>
          {checkAgainButton}
        </div>
      );
    }
    if (!review) {
      return (
        <p className="p-4 text-sm text-muted-foreground" data-state="loading">
          Loading the photo review…
        </p>
      );
    }
    if (status === 'superseded') {
      // Addendum 1: a registration that moved since the read is said as such.
      const code = review.superseded?.reason ?? review.last_apply?.code ?? null;
      const movedTo = code === 'photo_moved' ? review.superseded?.to ?? null : null;
      const said = movedTo
        ? `This photo was moved to ${recordInSentence(movedTo)} by another review of the same photo; nothing was changed here.`
        : code && code !== 'photo_removed' && EQUIPMENT_EDIT_ERRORS[code]
          ? EQUIPMENT_EDIT_ERRORS[code]
          : catalog
            ? 'These catalog values no longer apply to the record.'
            : 'This photo is no longer on the record.';
      return (
        <p className="p-4 text-sm text-gray-700" data-state="superseded" title={code ? `Code: ${code}` : undefined}>
          {said}
        </p>
      );
    }
    if (status === 'reading') {
      return (
        <div className="flex flex-col items-start gap-3 p-4" data-state="reading">
          {notice && (
            <p className="text-sm text-blue-900" title={notice.code ? `Code: ${notice.code}` : undefined}>
              {notice.text}
            </p>
          )}
          <p className="text-sm text-gray-700">
            {gaveUp ? 'Still reading. The review will wait on this tab.' : 'Reading the label…'}
          </p>
          {gaveUp && checkAgainButton}
        </div>
      );
    }
    if (fix) {
      // FS-876: a held component-mismatch review offers its fix beside
      // Dismiss: the rows, the photo, what it does to the system's tonnage,
      // a reason, and one button named after the records it changes.
      const said = catalogHeldWords({
        status: 'held',
        holdReason: review.hold?.reason,
        model: catalogModel,
        equipmentType: review.catalog?.equipment_type,
        droppedOn: review.dropped_on,
      });
      const wrongTab = fix.wrong.kind ? TAB_WORDS[fix.wrong.kind] : 'this';
      const rightTab = fix.right.kind ? TAB_WORDS[fix.right.kind] : 'other';
      const photoTitle = photoBlock
        ? fixPhotoBlockWords(photoBlock, fix)
        : photoAction === 'remove'
          ? `Take the photo off the ${wrongTab} record as a wrong photo, after the values are cleared. It can be put back with Undo on that card.`
          : `The photo moves to the ${rightTab} record with the values.`;
      return (
        <div className="space-y-4 p-4" data-state="held" data-testid="review-fix">
          {notice && (
            <p
              className="rounded-md border border-status-warning-border bg-status-warning p-3 text-sm text-status-warning-foreground"
              role="status"
              title={notice.code ? `Code: ${notice.code}` : undefined}
            >
              {notice.text}
            </p>
          )}
          <p className="text-sm text-gray-800" title={`Code: ${said?.code ?? 'held'}`}>
            {said?.text}
          </p>
          {fix.action === null ? (
            <>
              <p className="text-sm text-gray-700" data-testid="review-fix-note" title={`Code: ${fix.reason ?? 'none'}`}>
                {fixReasonWords(fix, catalogModel)}
              </p>
              <p className="text-xs text-muted-foreground">Nothing on the record was changed.</p>
              <div className="flex gap-2">{dismissButton}</div>
            </>
          ) : (
            <>
              <table className="min-w-full text-sm" data-testid="review-fix-rows">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-1 pr-2 font-medium">Use</th>
                    <th className="py-1 pr-3 font-medium">Field</th>
                    <th className="py-1 pr-3 font-medium">{`On the ${wrongTab} record`}</th>
                    <th className="py-1 font-medium">What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {fix.rows.map((row) => {
                    const label = fixFieldLabel(fix.wrong.kind, row.field);
                    const verb = row.effect === 'move' ? 'Move' : 'Clear';
                    const title =
                      row.state === 'locked_person_value'
                        ? LOCKED_PERSON_TITLE
                        : row.state === 'locked_required'
                          ? `The model number is what this review is about, so it always goes: ${fixRowWords(fix, row).toLowerCase()}`
                          : row.effect === 'move'
                            ? `Move this ${label.toLowerCase()} to the ${rightTab} record`
                            : `Clear this ${label.toLowerCase()} from the ${wrongTab} record`;
                    return (
                      <ReviewRow
                        key={row.field}
                        field={row.field}
                        stale={fixStale.has(row.field)}
                        box={{
                          id: `${headingId}-fix-${row.field}`,
                          label: `${verb} ${label}`,
                          checked: fixTicked(row),
                          disabled: row.state !== 'selectable' || busy,
                          title,
                          onChange: (on) => setFixChecks((prev) => ({ ...prev, [row.field]: on })),
                        }}
                      >
                        <td className="py-1 pr-3 align-top">{label}</td>
                        <td className="py-1 pr-3 align-top text-gray-600">
                          {fixValueText(fix.wrong.kind, row.field, row.value)}
                          {row.source && row.state !== 'locked_person_value' && (
                            <span className="block text-xs text-muted-foreground">{sourceWords(row.source)}</span>
                          )}
                        </td>
                        <td className="py-1 align-top text-xs text-gray-700">
                          <span className="block">{fixRowWords(fix, row)}</span>
                          {row.from_other_photo && (
                            <span className="block text-amber-800" data-testid={`review-fix-other-photo-${row.field}`}>
                              Read from a label photo the model number is not known to come from; it may be this record&apos;s own.
                            </span>
                          )}
                          {row.companions.map((c) => (
                            <span key={c.field} className="block text-muted-foreground">
                              {`${fixFieldLabel(fix.wrong.kind, c.field)} ${fixValueText(fix.wrong.kind, c.field, c.value)} ${
                                c.effect === 'move' ? 'moves with it' : 'is cleared with it'
                              }`}
                            </span>
                          ))}
                        </td>
                      </ReviewRow>
                    );
                  })}
                </tbody>
              </table>

              {fixNames.length > 0 && (
                <div className="space-y-1 text-sm" data-testid="review-fix-names">
                  {fixNames.map((name) => {
                    const key = fixNameKey(name);
                    const words = fixNameWords(name);
                    const available = nameAvailable(name);
                    const title = available
                      ? words.title
                      : `Tick ${name.named_for.map((f) => `"${fixFieldLabel(fix.wrong.kind, f)}"`).join(' or ')} above to change this name too: it is named for that value.`;
                    const noteId = `${headingId}-fix-${key}-note`;
                    return (
                      <div
                        key={key}
                        className={fixStale.has(key) ? 'rounded-sm bg-status-warning ring-1 ring-status-warning-border px-1' : undefined}
                        data-stale={fixStale.has(key) ? 'true' : undefined}
                      >
                        <label htmlFor={`${headingId}-fix-${key}`} className="flex items-center gap-2" title={title}>
                          <Checkbox
                            id={`${headingId}-fix-${key}`}
                            data-testid={`review-fix-name-${name.record}`}
                            checked={nameTicked(name)}
                            disabled={busy || !available}
                            aria-describedby={words.note ? noteId : undefined}
                            onCheckedChange={(on) => {
                              setFixChecks((prev) => ({ ...prev, [key]: on }));
                            }}
                            title={title}
                          />
                          <span className={available ? undefined : 'text-muted-foreground'}>{words.label}</span>
                        </label>
                        {words.note && (
                          <span id={noteId} className="ml-6 block text-xs text-muted-foreground">
                            {words.note}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {fix.photo && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <label htmlFor={`${headingId}-fix-photo`} className="flex items-center gap-2" title={photoTitle}>
                    <Checkbox
                      id={`${headingId}-fix-photo`}
                      data-testid="review-fix-photo"
                      checked={photoGoes}
                      disabled={photoBlock !== null || busy}
                      onCheckedChange={setPhotoTick}
                      title={photoTitle}
                    />
                    <span className={photoBlock ? 'text-muted-foreground' : undefined}>{fixPhotoLabel(fix, photoAction)}</span>
                  </label>
                  <a
                    href={fix.photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-700 underline"
                    title="Open the label photo in a new tab"
                  >
                    View photo
                  </a>
                </div>
              )}

              {consequenceText && (
                <p className="text-sm text-gray-800" data-testid="review-fix-consequence">
                  {consequenceText}
                </p>
              )}

              {fix.catalog_review_after === true && (
                <p className="text-sm text-gray-800" data-testid="review-fix-catalog-after">
                  {fixCatalogAfterWords(catalogModel, rightTab)}
                </p>
              )}

              <div>
                <label htmlFor={`${headingId}-fix-reason`} className="text-xs uppercase tracking-wide text-muted-foreground">
                  Reason for this change
                </label>
                <Input
                  id={`${headingId}-fix-reason`}
                  type="text"
                  value={fixReason}
                  disabled={busy}
                  title="Kept with the change in the edit history: say why these values were on the wrong record"
                  aria-describedby={`${headingId}-fix-reason-help${reasonError ? ` ${headingId}-fix-reason-error` : ''}`}
                  aria-invalid={Boolean(reasonError)}
                  onChange={(e) => {
                    setFixReason(e.target.value);
                    if (reasonError && e.target.value.trim().length >= MIN_REASON_LENGTH) setReasonError(null);
                  }}
                  className="mt-1"
                />
                <span id={`${headingId}-fix-reason-help`} className="mt-1 block text-xs text-muted-foreground">
                  Say why these values were on the wrong record; this reason is kept in the edit history.
                </span>
                {reasonError && (
                  <span id={`${headingId}-fix-reason-error`} className="mt-1 block text-xs text-red-700" role="alert">
                    {reasonError}
                  </span>
                )}
              </div>

              <div className="sticky bottom-0 z-10 flex flex-wrap gap-2 border-t bg-popover py-2">
                <Button
                  variant="primary"
                  onClick={() => void fixIt()}
                  disabled={busy || fixBlocker !== null}
                  title={
                    fixBlocker ??
                    (fixMoves
                      ? `Write the ticked values to the ${fix.right.exists ? '' : 'new '}${rightTab} record and take them off the ${wrongTab} record`
                      : `Take the ticked values off the ${wrongTab} record; the ${rightTab} record keeps its own`)
                  }
                >
                  {sending ? 'Working…' : fixActionLabel(fix, fixMoves)}
                </Button>
                {dismissButton}
              </div>
            </>
          )}
        </div>
      );
    }
    if (catalog && (status === 'held' || (status === 'failed' && !applyFailed))) {
      // FS-787: a held or failed catalog review has nothing to apply; it is
      // only ever dismissed.
      const said = catalogHeldWords({
        status: status as string,
        holdReason: review.hold?.reason,
        model: catalogModel,
        equipmentType: review.catalog?.equipment_type,
        droppedOn: review.dropped_on,
      });
      const error = (review.stages?.catalog as { error?: string } | undefined)?.error;
      return (
        <div className="space-y-3 p-4" data-state={status ?? undefined}>
          <p className="text-sm text-gray-800" title={`Code: ${error ?? said?.code ?? status}`}>
            {said?.text}
          </p>
          <p className="text-xs text-muted-foreground">Nothing on the record was changed.</p>
          <div className="flex gap-2">{dismissButton}</div>
        </div>
      );
    }
    if (status === 'held') {
      const held = heldWords(review.hold?.reason);
      return (
        <div className="space-y-3 p-4" data-state="held">
          <p className="text-sm text-gray-800" title={`Code: ${held.code}`}>
            {held.text}
          </p>
          <p className="text-xs text-muted-foreground">Nothing can be applied from this photo. It stays on the record.</p>
          <div className="flex gap-2">{dismissButton}</div>
        </div>
      );
    }
    if (status === 'failed' && !applyFailed) {
      const error = (review.stages?.read as { error?: string } | undefined)?.error;
      return (
        <div className="space-y-3 p-4" data-state="failed">
          <p className="text-sm text-red-800" title={`Code: ${error ?? 'failed'}`}>
            {error && EQUIPMENT_EDIT_ERRORS[error]
              ? EQUIPMENT_EDIT_ERRORS[error]
              : `The label couldn't be read: ${error ?? 'no reason was recorded'}`}
          </p>
          <div className="flex gap-2">{dismissButton}</div>
        </div>
      );
    }
    if (status === 'declined' || status === 'applied') {
      return (
        <p className="p-4 text-sm text-gray-700" data-state="done">
          {fixDecisionWords(review.decision) ?? 'This review is finished.'}
        </p>
      );
    }
    return rowsBody;
  })();

  return (
    <Dialog open onOpenChange={(open) => { if (!open && canClose) onClose(); }} disablePointerDismissal={sending}>
      <DialogContent
        ref={dialogRef}
        customWidth
        showCloseButton={false}
        aria-labelledby={headingId}
        initialFocus={() => dialogRef.current}
        finalFocus={returnFocus}
        data-testid="photo-review-dialog"
        className="!flex max-h-[90vh] w-full max-w-3xl flex-col overflow-clip gap-0 p-0"
      >
        {header}
        {errorBanner}
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoReviewDialog;
